import type { Appearance } from '@clerk/shared/types';

/**
 * Shared Clerk appearance config. Applied once on
 * `<ClerkProvider appearance={clerkAppearance}>` so every prebuilt auth surface
 * (`<SignIn>`, `<SignUp>`, `<Waitlist>`, `<UserButton>`) matches the
 * shadcn/Tailwind design system.
 *
 * Strategy:
 * - `variables` carry only font + radius. Colors are intentionally NOT set here
 *   because Clerk derives shade scales from color variables, and feeding it
 *   `var(--token)` (our oklch CSS custom properties) can break that math.
 * - `elements` carry Tailwind utility classes built from the same design tokens
 *   the shadcn components use, so each Clerk element renders like its shadcn
 *   counterpart and inherits dark mode via the `.dark` class automatically.
 *
 * Element-key coverage below targets the high-impact surfaces. To style an
 * element that still looks off, inspect its stable `cl-<elementKey>` class in
 * the browser and add that key here with Tailwind classes from the same tokens.
 */
export const clerkAppearance = {
    variables: {
        // Mirrors `--radius` in src/style.css. Kept as a concrete value because
        // Clerk derives sm/lg radii from this and may not evaluate `var()`.
        borderRadius: '0.625rem',
        fontFamily: 'inherit',
    },
    elements: {
        rootBox: 'w-full',
        card: 'bg-card text-card-foreground border border-border shadow-sm rounded-xl',
        headerTitle: 'text-foreground',
        headerSubtitle: 'text-muted-foreground',
        socialButtonsBlockButton:
            'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground rounded-md',
        socialButtonsBlockButtonText: 'text-foreground font-medium',
        dividerLine: 'bg-border',
        dividerText: 'text-muted-foreground',
        formFieldLabel: 'text-foreground',
        formFieldInput:
            'bg-background border border-input text-foreground rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0',
        formButtonPrimary:
            'bg-primary text-primary-foreground hover:bg-primary/90 rounded-md normal-case font-medium shadow-sm',
        footerActionText: 'text-muted-foreground',
        footerActionLink: 'text-primary hover:text-primary/90 font-medium',
        identityPreviewText: 'text-foreground',
        formResendCodeLink: 'text-primary',
    },
} satisfies Appearance;
